import { lazy, Suspense } from 'react';
import { emporiModule } from '@empori/util';
import {
  BrandModelData,
  BrandModelItem,
  BrandPagesConfig,
  CounterJson,
  CountdownClockJson,
  FormDetails,
  GoogleMapsJson,
  HiddenTextJson,
  ImageGalleryModel,
  ImageWithInfo,
  InputItem,
  InteractiveImageObj,
  RowAnimationStyle,
  LogotypesJson,
  PopupViewModel,
  ThemePageSlideshowJson,
  SiteSearchConfig
} from './empracoTypes';
import { LoadingSkeleton } from './generic/elements';
import { ThemeIds } from './util/util';

export function render(element: JSX.Element, container: Element | DocumentFragment | null) {
  import(/* webpackChunkName: "react-dom-render" */'react-dom')
    .then(r => r.render(element, container));
}

const GoogleMaps = lazy(() =>
  import(/* webpackChunkName: "google-maps" */'./controls/GoogleMaps'));
const ThumbnailImageGallery = lazy(() =>
  import(/* webpackChunkName: "thumbnail-image-gallery" */'./controls/ImageGalleryWithThumbnails'));
const ReadTime = lazy(() =>
  import(/* webpackChunkName: "read-time" */'./controls/ReadTime'));
const Logotypes = lazy(() =>
  import(/* webpackChunkName: "logotypes" */'./controls/logotypes/logotypes'));
const ThemePageSlideshow = lazy(() =>
  import(/* webpackChunkName: "theme-page-slideshow" */'./controls/ThemePageSlideshow'));
const FormCheckboxes = lazy(() =>
  import(/* webpackChunkName: "form-checkboxes" */'./controls/Forms/FormSelectorCheckboxes'));
const SiteSearch = lazy(() =>
  import(/* webpackChunkName: "site-search" */'./controls/site-search/site-search'));
const BrandListing = lazy(() =>
  import(/* webpackChunkName: "brand-listing" */'./controls/Brands/brandListing'));
const InteractiveImage = lazy(() =>
  import(/* webpackChunkName: "interactive-image" */'./controls/InteractiveImage'));

emporiModule('emporiLoaded', {
  controls: {
    InitGoogleMaps(element: string, model: GoogleMapsJson) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <GoogleMaps model={model} />
        </Suspense>, document.querySelector(element));
    },
    InitHiddenText(contentElement: string, buttonElement: string, model: HiddenTextJson) {
      import(/* webpackChunkName: "hidden-text" */'./controls/HiddenText')
        .then(hiddenText => hiddenText.default(contentElement, buttonElement, model));
    },
    InitMainNavigation(
      mainNav: HTMLDivElement,
      mobileTrigger: HTMLDivElement,
      secNav: HTMLDivElement,
      secTriggerer: HTMLDivElement,
      searchbar: HTMLDivElement,
      scrollThreshold?: number) {
      import(/* webpackChunkName: "main-navigation" */'./controls/MainNavigation')
        .then(nav => nav.default(mainNav, mobileTrigger, secNav, secTriggerer, searchbar, scrollThreshold));
    },
    InitImageGallery(element: HTMLDivElement, model: ImageGalleryModel) {
      import(/* webpackChunkName: "image-gallery" */'./controls/ImageGallery')
        .then(gallery => gallery.default(element, model));
    },
    InitThumbnailImageGallery(element: string, images: ImageWithInfo[]) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <ThumbnailImageGallery images={images} />
        </Suspense>, document.getElementById(element));
    },
    InitNewsArticle(element: string, calcEl: string) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <ReadTime textToCalc={calcEl} />
        </Suspense>, document.querySelector(element));
    },
    InitBaseCompositionPageListGallery(element: HTMLDivElement, slidesPerViewOnLargeScreen: number = 4) {
      import(/* webpackChunkName: "base-page-list-slideshow" */'./controls/BaseCompositionPageListGallery')
        .then(gallery => gallery.default(element, slidesPerViewOnLargeScreen));
    },
    InitCounter(counter: CounterJson) {
      import(/* webpackChunkName: "counter" */'./controls/Counter')
        .then(count => count.default(counter));
    },
    InitCountdownClock(countdown: CountdownClockJson) {
      import(/* webpackChunkName: "countdown-clock" */'./controls/CountdownClock')
        .then(clock => clock.default(countdown));
    },
    InitLogotypes(element: string, data: LogotypesJson) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <Logotypes data={data} />
        </Suspense>, document.getElementById(element));
    },
    InitFormSelectorFns(formDetails: FormDetails) {
      import(/* webpackChunkName: "form-fns" */'./controls/Forms/FormSelectorFns')
        .then(fns => fns.default(formDetails));
    },
    InitFormCheckboxes(element: string, input: InputItem) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <FormCheckboxes input={input} />
        </Suspense>, document.getElementById(element));
    },
    InitFaqItems(element: string, enableCollapse: boolean) {
      import(/* webpackChunkName: "faq-items" */'./controls/FaqItem')
        .then(faqItems => faqItems.default(element, enableCollapse));
    },
    InitGridFunctions(themeId: ThemeIds) {
      import(/* webpackChunkName: "grid-fns" */'./generic/GridFunctions')
        .then(grid => grid.default(themeId));
    },
    InitThemePageSlideshow(element: string, items: ImageWithInfo[], theme: ThemePageSlideshowJson) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <ThemePageSlideshow items={items} rawTheme={theme.rawTheme} pureTheme={theme.pureTheme} />
        </Suspense>, document.getElementById(element));
    },
    InitScrollToTop() {
      import(/* webpackChunkName: "scroll-tt" */'./controls/ScrollToTop')
        .then(sctt => sctt.default());
    },
    InitSiteSearch(element: string, config: SiteSearchConfig) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <SiteSearch parentId={element} config={config} />
        </Suspense>,
        document.getElementById(element)
      );
    },
    InitBrandModelForm(modelData: BrandModelData) {
      import(/* webpackChunkName: "brand-form" */'./controls/Forms/alterBrandModelForm')
        .then(fn => fn.default(modelData));
    },
    InitBrandListing(element: string, items: BrandModelItem[], config: BrandPagesConfig) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <BrandListing items={items} config={config} />
        </Suspense>,
        document.getElementById(element)
      );
    },
    InitRowAnimation(animationStyle: RowAnimationStyle) {
      import(/* webpackChunkName: "row-animation" */'./generic/rowAnimation')
        .then(animation => animation.default(animationStyle));
    },
    InitInteractiveImage(element: string, items: InteractiveImageObj) {
      render(
        <Suspense fallback={<LoadingSkeleton />}>
          <InteractiveImage {...items} />
        </Suspense>,
        document.getElementById(element)
      );
    },
    InitPopup(props: PopupViewModel) {
      import(/* webpackChunkName: "popup" */'./controls/Popup')
        .then(popup => popup.default(props));
    },
    InitDecorativeImage(element: string) {
      import(/* webpackChunkName: "decorative-img" */'./controls/DecorativeImage')
        .then(gallery => gallery.default(element));
    },
    InitWebshopNavigation(showLessTranslation: string, showMoreTranslation: string) {
      import(/* webpackChunkName: "webshop-navigation" */'./controls/webshop-navigation')
        .then(gallery => gallery.default(showLessTranslation, showMoreTranslation));
    }
  }
});